import React, { useState, useEffect, useCallback } from 'react';
import { productsAPI, salesAPI, promotionsAPI, membersAPI, settingsAPI } from '../services/api';
import io from 'socket.io-client';
import Swal from 'sweetalert2';
import { SOCKET_URL } from '../config/socket';
import NumericKeyboard from '../components/NumericKeyboard';

const socket = io(SOCKET_URL);

// Add socket connection logging
socket.on('connect', () => {
  console.log('POS Socket connected! ID:', socket.id);
});

socket.on('disconnect', () => {
  console.log('POS Socket disconnected!');
});

socket.on('connect_error', (error) => {
  console.error('POS Socket connection error:', error);
});

function POS() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paidAmount, setPaidAmount] = useState(0);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [manualDiscountType, setManualDiscountType] = useState('baht'); // 'baht' or 'percent'
  const [manualDiscountValue, setManualDiscountValue] = useState(0);
  const [cartPromotion, setCartPromotion] = useState(null); // Cart-wide promotion
  const searchInputRef = React.useRef(null);
  const confirmKeyHandlerRef = React.useRef(null);
  const successKeyHandlerRef = React.useRef(null);
  const isDialogOpenRef = React.useRef(false);

  // Member states
  const [memberPhone, setMemberPhone] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [pointsDiscount, setPointsDiscount] = useState(0); // Discount from redeemed points
  const [redeemedPoints, setRedeemedPoints] = useState(0); // Number of points redeemed
  const memberSectionRef = React.useRef(null);

  // Settings states
  const [settings, setSettings] = useState({
    redemption_points: 100,
    redemption_value: 10,
    points_earn_baht: 100,
    points_earn_points: 1
  });

  // Numeric Keyboard states
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardType, setKeyboardType] = useState(''); // 'phone', 'paid', 'discount'
  const [keyboardValue, setKeyboardValue] = useState(0);

  // Calculate functions - MUST be defined before useEffect
  const calculateSubtotal = useCallback(() => {
    return cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  }, [cart]);

  const calculatePromoDiscount = useCallback(() => {
    const itemDiscounts = cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
    const cartDiscount = cartPromotion ? cartPromotion.discount : 0;
    return itemDiscounts + cartDiscount;
  }, [cart, cartPromotion]);

  const calculateManualDiscount = useCallback(() => {
    const subtotal = calculateSubtotal();
    if (manualDiscountType === 'baht') {
      return Math.min(manualDiscountValue, subtotal);
    } else {
      return (subtotal * manualDiscountValue) / 100;
    }
  }, [manualDiscountType, manualDiscountValue, calculateSubtotal]);

  const calculateDiscount = useCallback(() => {
    return calculatePromoDiscount() + calculateManualDiscount() + pointsDiscount;
  }, [calculatePromoDiscount, calculateManualDiscount, pointsDiscount]);

  const calculateTotal = useCallback(() => {
    return Math.max(0, calculateSubtotal() - calculateDiscount());
  }, [calculateSubtotal, calculateDiscount]);

  const addToCart = useCallback(async (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    let newCart;

    if (existingItem) {
      newCart = cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      const sellingPrice = parseFloat(product.selling_price);
      newCart = [...cart, {
        product_id: product.id,
        product_name: product.name,
        unit_price: sellingPrice,
        quantity: 1,
        discount_amount: 0,
        promotion_id: null,
      }];
    }

    try {
      const cartSubtotal = newCart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
      const memberId = selectedMember ? selectedMember.id : null;

      const updatedCart = [...newCart];
      for (let i = 0; i < updatedCart.length; i++) {
        const item = updatedCart[i];
        const promoResponse = await promotionsAPI.calculateDiscount({
          product_id: item.product_id,
          quantity: item.quantity,
          total_amount: item.unit_price * item.quantity,
          cart_total: cartSubtotal,
          member_id: memberId
        });

        if (promoResponse.data.discount > 0) {
          updatedCart[i] = {
            ...item,
            discount_amount: promoResponse.data.discount,
            promotion_id: promoResponse.data.promotion?.id || null,
          };
        } else {
          updatedCart[i] = {
            ...item,
            discount_amount: 0,
            promotion_id: null,
          };
        }
      }

      const cartPromoResponse = await promotionsAPI.calculateCartDiscount({
        cart_total: cartSubtotal,
        member_id: memberId
      });

      if (cartPromoResponse.data.discount > 0) {
        setCartPromotion(cartPromoResponse.data);
      } else {
        setCartPromotion(null);
      }

      setCart(updatedCart);
    } catch (error) {
      console.error('Error checking promotions:', error);
      setCart(newCart);
    }
  }, [cart, selectedMember]);

  useEffect(() => {
    fetchProducts();
    fetchSettings();
    // Focus search input on mount
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Version check
    console.log('POS v3.1 - 4 columns + No scroll - 2025-11-10 00:00');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus search input after keyboard closes or at any time
  useEffect(() => {
    if (!showKeyboard && searchInputRef.current && !isDialogOpenRef.current) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [showKeyboard]);

  // Barcode scanner listener
  useEffect(() => {
    let buffer = '';
    let bufferTimeout = null;

    const handleKeyPress = (e) => {
      // Clear existing timeout
      if (bufferTimeout) {
        clearTimeout(bufferTimeout);
      }

      // Ignore if user is typing in an input field (except the search field which should allow barcode scanning)
      if ((e.target.tagName === 'INPUT' && e.target.placeholder !== 'ค้นหาสินค้า หรือ สแกนบาร์โค้ด...') ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.tagName === 'SELECT') {
        return;
      }

      if (e.key === 'Enter' && buffer.length > 0) {
        // Barcode scan complete
        const barcode = buffer.trim();
        buffer = '';

        // Find product by barcode
        const product = products.find(p => p.barcode === barcode);

        if (product) {
          addToCart(product);
          // Visual feedback
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE');
          audio.play().catch(() => {}); // Ignore audio errors

          // Clear search field if barcode was typed there
          if (e.target.placeholder === 'ค้นหาสินค้า หรือ สแกนบาร์โค้ด...') {
            setSearch('');
          }

          // Return focus to search input after adding to cart
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 100);
        } else {
          // Product not found - show error
          Swal.fire({
            icon: 'error',
            title: 'ไม่พบสินค้า',
            text: 'ไม่พบสินค้าบาร์โค้ด: ' + barcode,
            confirmButtonColor: '#0ea5e9'
          });
        }
      } else if (e.key.length === 1) {
        // Add character to buffer
        buffer += e.key;

        // Reset buffer after 100ms of inactivity (barcode scanners type very fast)
        bufferTimeout = setTimeout(() => {
          buffer = '';
        }, 100);
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => {
      window.removeEventListener('keypress', handleKeyPress);
      if (bufferTimeout) {
        clearTimeout(bufferTimeout);
      }
    };
  }, [products, addToCart]);

  useEffect(() => {
    // Update customer display when cart changes
    socket.emit('update-cart', {
      items: cart,
      total: calculateTotal(),
      subtotal: calculateSubtotal(),
      paymentMethod: paymentMethod,
      memberName: selectedMember ? selectedMember.full_name : null,
      cartPromotion: cartPromotion,
      manualDiscountType: manualDiscountType,
      manualDiscountValue: manualDiscountValue,
      pointsDiscount: pointsDiscount
    });
  }, [cart, paymentMethod, selectedMember, cartPromotion, manualDiscountType, manualDiscountValue, pointsDiscount, calculateSubtotal, calculateTotal]);

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ status: 'active' });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings({
        redemption_points: response.data.redemption_points || 100,
        redemption_value: response.data.redemption_value || 10,
        points_earn_baht: response.data.points_earn_baht || 100,
        points_earn_points: response.data.points_earn_points || 1,
        store_name: response.data.store_name || 'ร้านค้า POS',
        store_address: response.data.store_address || '',
        store_phone: response.data.store_phone || '',
        tax_id: response.data.tax_id || '',
        store_logo: response.data.store_logo || ''
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Recalculate all promotions in cart (for when member is selected/cleared)
  const recalculateAllPromotions = async (memberId = null) => {
    if (cart.length === 0) {
      setCartPromotion(null);
      return;
    }

    try {
      const updatedCart = [...cart];
      const cartSubtotal = updatedCart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);

      // Recalculate per-product promotions
      for (let i = 0; i < updatedCart.length; i++) {
        const item = updatedCart[i];
        const promoResponse = await promotionsAPI.calculateDiscount({
          product_id: item.product_id,
          quantity: item.quantity,
          total_amount: item.unit_price * item.quantity,
          cart_total: cartSubtotal,
          member_id: memberId
        });

        if (promoResponse.data.discount > 0) {
          updatedCart[i] = {
            ...item,
            discount_amount: promoResponse.data.discount,
            promotion_id: promoResponse.data.promotion?.id || null,
          };
        } else {
          // Clear discount if promotion no longer applies
          updatedCart[i] = {
            ...item,
            discount_amount: 0,
            promotion_id: null,
          };
        }
      }

      setCart(updatedCart);

      // Calculate cart-wide promotions
      const cartPromoResponse = await promotionsAPI.calculateCartDiscount({
        cart_total: cartSubtotal,
        member_id: memberId
      });

      if (cartPromoResponse.data.discount > 0) {
        setCartPromotion(cartPromoResponse.data);
      } else {
        setCartPromotion(null);
      }
    } catch (error) {
      console.error('Error recalculating promotions:', error);
    }
  };

  // Member functions
  const searchMember = async (phoneNumber = null) => {
    // Use parameter if provided, otherwise use state
    const phone = phoneNumber || memberPhone;

    if (!phone || phone.trim() === '') {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกเบอร์โทร',
        confirmButtonColor: '#0ea5e9'
      });
      return;
    }

    // Validate phone number format (9-10 digits)
    const phoneRegex = /^[0-9]{9,10}$/;
    if (!phoneRegex.test(phone.trim())) {
      Swal.fire({
        icon: 'warning',
        title: 'รูปแบบเบอร์โทรไม่ถูกต้อง',
        text: 'กรุณากรอกเบอร์โทร 9-10 หลัก',
        confirmButtonColor: '#0ea5e9'
      });
      return;
    }

    try {
      const response = await membersAPI.searchByPhone(phone);

      // Validate response data
      if (!response.data || !response.data.id || !response.data.full_name) {
        throw new Error('Invalid member data received');
      }

      setSelectedMember(response.data);

      // Recalculate ALL promotions with member ID (including existing cart items)
      if (cart.length > 0) {
        await recalculateAllPromotions(response.data.id);
      }

      Swal.fire({
        icon: 'success',
        title: 'พบข้อมูลสมาชิก',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>ชื่อ:</strong> ${response.data.full_name}</p>
            <p class="mb-2"><strong>เบอร์โทร:</strong> ${response.data.phone}</p>
            <p class="mb-2"><strong>คะแนนคงเหลือ:</strong> <span class="text-primary-600 font-bold text-xl">${response.data.points || 0}</span> คะแนน</p>
          </div>
        `,
        confirmButtonColor: '#0ea5e9'
      });
    } catch (error) {
      console.error('Search member error:', error);
      setSelectedMember(null);
      Swal.fire({
        icon: 'error',
        title: 'ไม่พบข้อมูลสมาชิก',
        text: 'เบอร์โทรนี้ยังไม่ได้ลงทะเบียนเป็นสมาชิก',
        confirmButtonColor: '#0ea5e9'
      });
    }
  };

  const clearMember = async () => {
    setSelectedMember(null);
    setMemberPhone('');
    setPointsDiscount(0);
    setRedeemedPoints(0);

    // Recalculate promotions without member ID
    await recalculateAllPromotions(null);
  };

  const calculateEarnedPoints = () => {
    // Calculate points based on settings: points_earn_baht and points_earn_points
    const total = calculateTotal();
    const earnBaht = settings.points_earn_baht || 100;
    const earnPoints = settings.points_earn_points || 1;
    return Math.floor((total / earnBaht) * earnPoints * 100) / 100; // Round to 2 decimals
  };

  const calculatePointDiscount = (pointsToUse) => {
    // ใช้ค่าจาก settings: redemption_points คะแนน = redemption_value บาท
    return (pointsToUse / settings.redemption_points) * settings.redemption_value;
  };

  const redeemPoints = async () => {
    if (!selectedMember) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาเลือกสมาชิก',
        text: 'ต้องเลือกสมาชิกก่อนแลกคะแนน',
        confirmButtonColor: '#0ea5e9'
      });
      return;
    }

    if (selectedMember.points === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'ไม่มีคะแนน',
        text: 'สมาชิกไม่มีคะแนนให้แลก',
        confirmButtonColor: '#0ea5e9'
      });
      return;
    }

    const subtotal = calculateSubtotal();
    const promoDiscount = calculatePromoDiscount();
    const manualDiscount = calculateManualDiscount();
    const currentTotal = subtotal - promoDiscount - manualDiscount;

    // Calculate max points that can be used (cannot exceed total amount)
    // ตัวอย่าง: ถ้า redemption_points=100, redemption_value=10
    // แปลว่า 100 คะแนน = 10 บาท, ดังนั้น max points = (total * redemption_points) / redemption_value
    const maxPointsBasedOnTotal = Math.floor((currentTotal * settings.redemption_points) / settings.redemption_value);
    // ปัดเป็นจำนวนที่หารด้วย redemption_points ลงตัว
    const maxPointsRounded = Math.floor(maxPointsBasedOnTotal / settings.redemption_points) * settings.redemption_points;
    const maxPoints = Math.min(selectedMember.points, maxPointsRounded);

    const minRedeemPoints = settings.redemption_points;

    const { value: pointsToUse } = await Swal.fire({
      title: 'แลกคะแนน',
      html: `
        <div class="text-left mb-4">
          <p class="mb-2">คะแนนที่มี: <strong class="text-yellow-600">${selectedMember.points}</strong> คะแนน</p>
          <p class="mb-2">อัตราแลก: <strong>${settings.redemption_points} คะแนน = ${settings.redemption_value} บาท</strong></p>
          <p class="mb-2">ยอดที่สามารถใช้คะแนนได้: <strong>฿${currentTotal.toFixed(2)}</strong></p>
          <p class="mb-2">คะแนนที่สามารถใช้ได้สูงสุด: <strong>${maxPoints}</strong> คะแนน</p>
          <p class="text-sm text-gray-500">ส่วนลดสูงสุด: ฿${calculatePointDiscount(maxPoints).toFixed(2)}</p>
        </div>
      `,
      input: 'number',
      inputLabel: 'จำนวนคะแนนที่ต้องการใช้',
      inputPlaceholder: 'กรอกจำนวนคะแนน',
      inputAttributes: {
        min: 0,
        max: maxPoints,
        step: minRedeemPoints
      },
      showCancelButton: true,
      showDenyButton: maxPoints >= minRedeemPoints,
      confirmButtonText: 'ยืนยัน',
      denyButtonText: `แลกทั้งหมด (${maxPoints} คะแนน)`,
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#0ea5e9',
      denyButtonColor: '#f59e0b',
      inputValidator: (value) => {
        const points = parseInt(value);
        if (!value || points <= 0) {
          return 'กรุณากรอกจำนวนคะแนน';
        }
        if (points > maxPoints) {
          return `สามารถใช้ได้สูงสุด ${maxPoints} คะแนน`;
        }
        if (points % minRedeemPoints !== 0) {
          return `ต้องเป็นจำนวนคะแนนที่หาร ${minRedeemPoints} ลงตัว`;
        }
        return null;
      },
      preDeny: () => {
        return maxPoints;
      }
    });

    if (pointsToUse) {
      const points = parseInt(pointsToUse);
      const discount = calculatePointDiscount(points);
      setPointsDiscount(discount);
      setRedeemedPoints(points);

      Swal.fire({
        icon: 'success',
        title: 'แลกคะแนนสำเร็จ',
        html: `
          <div class="text-left">
            <p class="mb-2">ใช้คะแนน: <strong>${points}</strong> คะแนน</p>
            <p class="mb-2 text-green-600 font-bold text-xl">ส่วนลด: ฿${discount.toFixed(2)}</p>
            <p class="text-sm text-gray-500">*คะแนนจะถูกหักเมื่อชำระเงินสำเร็จ</p>
          </div>
        `,
        confirmButtonColor: '#0ea5e9',
        timer: 3000
      });
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      // Update quantity first
      let updatedCart;
      setCart(prevCart => {
        updatedCart = prevCart.map(item =>
          item.product_id === productId
            ? { ...item, quantity: newQuantity }
            : item
        );
        return updatedCart;
      });

      // Recalculate ALL promotions for ALL items (because cart_total changed)
      try {
        const cartSubtotal = updatedCart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
        const memberId = selectedMember ? selectedMember.id : null;

        // Recalculate promotions for ALL items in cart
        const finalCart = [...updatedCart];
        for (let i = 0; i < finalCart.length; i++) {
          const item = finalCart[i];
          const promoResponse = await promotionsAPI.calculateDiscount({
            product_id: item.product_id,
            quantity: item.quantity,
            total_amount: item.unit_price * item.quantity,
            cart_total: cartSubtotal,
            member_id: memberId
          });

          if (promoResponse.data.discount > 0) {
            finalCart[i] = {
              ...item,
              discount_amount: promoResponse.data.discount,
              promotion_id: promoResponse.data.promotion?.id || null,
            };
          } else {
            finalCart[i] = {
              ...item,
              discount_amount: 0,
              promotion_id: null,
            };
          }
        }

        setCart(finalCart);

        // Recalculate cart-wide promotions
        const cartPromoResponse = await promotionsAPI.calculateCartDiscount({
          cart_total: cartSubtotal,
          member_id: memberId
        });

        if (cartPromoResponse.data.discount > 0) {
          setCartPromotion(cartPromoResponse.data);
        } else {
          setCartPromotion(null);
        }
      } catch (error) {
        console.error('Error recalculating promotion:', error);
      }
    }
  };

  const removeFromCart = async (productId) => {
    const newCart = cart.filter(item => item.product_id !== productId);
    setCart(newCart);

    // Recalculate ALL promotions after removal
    if (newCart.length > 0) {
      try {
        const cartSubtotal = newCart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
        const memberId = selectedMember ? selectedMember.id : null;

        // Recalculate promotions for ALL remaining items in cart
        const updatedCart = [...newCart];
        for (let i = 0; i < updatedCart.length; i++) {
          const item = updatedCart[i];
          const promoResponse = await promotionsAPI.calculateDiscount({
            product_id: item.product_id,
            quantity: item.quantity,
            total_amount: item.unit_price * item.quantity,
            cart_total: cartSubtotal,
            member_id: memberId
          });

          if (promoResponse.data.discount > 0) {
            updatedCart[i] = {
              ...item,
              discount_amount: promoResponse.data.discount,
              promotion_id: promoResponse.data.promotion?.id || null,
            };
          } else {
            updatedCart[i] = {
              ...item,
              discount_amount: 0,
              promotion_id: null,
            };
          }
        }

        setCart(updatedCart);

        // Recalculate cart-wide promotions
        const cartPromoResponse = await promotionsAPI.calculateCartDiscount({
          cart_total: cartSubtotal,
          member_id: memberId
        });

        if (cartPromoResponse.data.discount > 0) {
          setCartPromotion(cartPromoResponse.data);
        } else {
          setCartPromotion(null);
        }
      } catch (error) {
        console.error('Error recalculating promotions:', error);
      }
    } else {
      setCartPromotion(null);
    }
  };

  const calculateChange = () => {
    return Math.max(0, paidAmount - calculateTotal());
  };

  // Numeric Keyboard Handlers
  const openKeyboard = (type) => {
    setKeyboardType(type);

    if (type === 'phone') {
      setKeyboardValue(memberPhone || '');
    } else if (type === 'paid') {
      setKeyboardValue(paidAmount || 0);
    } else if (type === 'discount') {
      setKeyboardValue(manualDiscountValue || 0);
    }

    setShowKeyboard(true);
  };

  const closeKeyboard = async () => {
    // Apply the value based on keyboard type
    if (keyboardType === 'phone') {
      const phoneValue = keyboardValue.toString();
      setMemberPhone(phoneValue);

      // Auto search member after closing keyboard
      if (phoneValue && phoneValue.length >= 9) {
        setShowKeyboard(false);
        setKeyboardType('');

        // Pass phoneValue directly to searchMember instead of waiting for state update
        setTimeout(() => {
          searchMember(phoneValue);
        }, 100);
        return;
      }
    } else if (keyboardType === 'paid') {
      setPaidAmount(parseFloat(keyboardValue) || 0);
    } else if (keyboardType === 'discount') {
      setManualDiscountValue(parseFloat(keyboardValue) || 0);
    }

    setShowKeyboard(false);
    setKeyboardType('');
  };

  const handleKeyboardChange = (value) => {
    setKeyboardValue(value);
  };

  // Convert image to base64
  const imageToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return null;
    }
  };

  // Generate receipt HTML (same format as Sales History page)
  const generateReceiptHTML = (sale, logoBase64 = null) => {
    const storeName = settings?.store_name || 'ร้านค้า POS';
    const storeAddress = settings?.store_address || '';
    const storePhone = settings?.store_phone || '';
    const taxId = settings?.tax_id || '';

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>ใบเสร็จ ${sale.sale_number}</title>
  <style>
    @media print {
      @page {
        size: 80mm auto;
        margin: 5mm;
      }
      body {
        margin: 0;
        padding: 0;
      }
    }
    body {
      font-family: 'Sarabun', 'Arial', sans-serif;
      font-size: 12px;
      width: 80mm;
      margin: 0 auto;
      padding: 10px;
    }
    .header {
      text-align: center;
      margin-bottom: 10px;
      border-bottom: 2px dashed #000;
      padding-bottom: 10px;
    }
    .store-logo {
      max-width: 25mm;
      max-height: 25mm;
      margin: 0 auto 5px auto;
      display: block;
    }
    .store-name {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .store-info {
      font-size: 10px;
      margin: 2px 0;
    }
    .receipt-info {
      margin: 10px 0;
      font-size: 11px;
    }
    .receipt-info div {
      margin: 3px 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
    }
    th {
      border-top: 1px dashed #000;
      border-bottom: 1px dashed #000;
      padding: 5px 0;
      text-align: left;
      font-size: 11px;
    }
    td {
      padding: 5px 0;
      font-size: 11px;
    }
    .text-right {
      text-align: right;
    }
    .text-center {
      text-align: center;
    }
    .total-section {
      border-top: 1px dashed #000;
      padding-top: 5px;
      margin-top: 5px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      margin: 3px 0;
    }
    .grand-total {
      font-size: 14px;
      font-weight: bold;
      border-top: 2px solid #000;
      padding-top: 5px;
      margin-top: 5px;
    }
    .footer {
      text-align: center;
      margin-top: 15px;
      padding-top: 10px;
      border-top: 2px dashed #000;
      font-size: 10px;
    }
  </style>
</head>
<body>
  <div class="header">
    ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="store-logo">` : ''}
    <div class="store-name">${storeName}</div>
    ${storeAddress ? `<div class="store-info">${storeAddress}</div>` : ''}
    ${storePhone ? `<div class="store-info">โทร: ${storePhone}</div>` : ''}
    ${taxId ? `<div class="store-info">เลขประจำตัวผู้เสียภาษี: ${taxId}</div>` : ''}
  </div>

  <div class="receipt-info">
    <div><strong>เลขที่ใบเสร็จ:</strong> ${sale.sale_number}</div>
    <div><strong>วันที่:</strong> ${new Date(sale.sale_date).toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })}</div>
    <div><strong>ลูกค้า:</strong> ${sale.customer_name}</div>
    <div><strong>พนักงาน:</strong> ${sale.cashier_name}</div>
    <div><strong>ชำระโดย:</strong> ${
      sale.payment_method === 'cash' ? 'เงินสด' :
      sale.payment_method === 'transfer' ? 'โอนเงิน' :
      sale.payment_method === 'card' ? 'บัตรเครดิต/เดบิต' :
      sale.payment_method
    }</div>
  </div>

  <table>
    <thead>
      <tr>
        <th>สินค้า</th>
        <th class="text-center">จำนวน</th>
        <th class="text-right">ราคา</th>
        <th class="text-right">รวม</th>
      </tr>
    </thead>
    <tbody>
      ${sale.items.map(item => `
        <tr>
          <td>${item.product_name}</td>
          <td class="text-center">${item.quantity}</td>
          <td class="text-right">฿${parseFloat(item.unit_price).toFixed(2)}</td>
          <td class="text-right">฿${parseFloat(item.subtotal).toFixed(2)}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <div class="total-section">
    <div class="total-row">
      <span>ยอดรวม:</span>
      <span>฿${parseFloat(sale.subtotal).toFixed(2)}</span>
    </div>
    ${(() => {
      // Calculate breakdown of discounts
      const totalDiscount = parseFloat(sale.discount_amount) || 0;
      const pointsDiscount = parseFloat(sale.points_discount) || 0;

      // Calculate promotion discount from items
      const promotionDiscount = sale.items.reduce((sum, item) => {
        return sum + (parseFloat(item.discount_amount) || 0);
      }, 0);

      // Other discount = total - points - promotion
      const otherDiscount = totalDiscount - pointsDiscount - promotionDiscount;

      let discountHTML = '';

      if (promotionDiscount > 0) {
        discountHTML += `
    <div class="total-row">
      <span>ส่วนลดโปรโมชั่น:</span>
      <span>-฿${promotionDiscount.toFixed(2)}</span>
    </div>`;
      }

      if (pointsDiscount > 0) {
        const pointsUsed = sale.points_used || 0;
        discountHTML += `
    <div class="total-row">
      <span>ส่วนลดจากคะแนน (${pointsUsed} คะแนน):</span>
      <span>-฿${pointsDiscount.toFixed(2)}</span>
    </div>`;
      }

      if (otherDiscount > 0) {
        discountHTML += `
    <div class="total-row">
      <span>ส่วนลดอื่นๆ:</span>
      <span>-฿${otherDiscount.toFixed(2)}</span>
    </div>`;
      }

      return discountHTML;
    })()}
    <div class="total-row grand-total">
      <span>ยอดสุทธิ:</span>
      <span>฿${parseFloat(sale.total_amount).toFixed(2)}</span>
    </div>
    ${sale.payment_method === 'cash' ? `
    <div class="total-row">
      <span>รับเงิน:</span>
      <span>฿${parseFloat(sale.paid_amount).toFixed(2)}</span>
    </div>
    <div class="total-row">
      <span>เงินทอน:</span>
      <span>฿${parseFloat(sale.change_amount).toFixed(2)}</span>
    </div>
    ` : ''}
  </div>

  ${sale.member_name ? `
  <div style="border-top: 2px dashed #000; padding-top: 10px; margin-top: 10px; font-size: 11px;">
    <div style="font-weight: bold; margin-bottom: 5px;">⭐ คะแนนสะสม</div>
    ${(() => {
      const earnedPoints = Math.floor(parseFloat(sale.total_amount) / 100);
      const usedPoints = sale.points_used || 0;
      const memberPoints = sale.member_points || 0;

      return `
    <div style="display: flex; justify-content: space-between; margin: 3px 0;">
      <span>คะแนนที่ได้รับ:</span>
      <span style="color: #16a34a; font-weight: bold;">+${earnedPoints} คะแนน</span>
    </div>
    ${usedPoints > 0 ? `
    <div style="display: flex; justify-content: space-between; margin: 3px 0;">
      <span>คะแนนที่ใช้:</span>
      <span style="color: #dc2626; font-weight: bold;">-${usedPoints} คะแนน</span>
    </div>
    ` : ''}
    <div style="display: flex; justify-content: space-between; margin: 3px 0; padding-top: 5px; border-top: 1px dashed #666;">
      <span style="font-weight: bold;">คะแนนคงเหลือ:</span>
      <span style="color: #0ea5e9; font-weight: bold;">${memberPoints.toLocaleString()} คะแนน</span>
    </div>
    ${(() => {
      // Calculate days until January 1st of next year (when points reset)
      const today = new Date();
      const currentYear = today.getFullYear();
      const nextJanuary1 = new Date(currentYear + 1, 0, 1); // January 1st next year
      const daysUntilReset = Math.ceil((nextJanuary1 - today) / (1000 * 60 * 60 * 24));

      // Show warning
      if (daysUntilReset > 0) {
        return `
    <div style="background-color: #fef3c7; border: 1px solid #f59e0b; border-radius: 4px; padding: 5px; margin-top: 5px;">
      <div style="color: #d97706; font-weight: bold; font-size: 10px; text-align: center;">
        ⚠️ คะแนนของคุณจะหมดอายุอีก ${daysUntilReset} วัน<br>
        แล้วระบบจะรีเซ็ทเป็น 0
      </div>
    </div>
        `;
      }
      return '';
    })()}
      `;
    })()}
  </div>
  ` : ''}

  <div class="footer">
    <div>ขอบคุณที่ใช้บริการ</div>
    <div>โปรดตรวจสอบสินค้าก่อนออกจากร้าน</div>
  </div>

  <script>
    window.onload = () => {
      setTimeout(() => {
        window.print();
      }, 250);
    };
  </script>
</body>
</html>
    `;
  };

  // Print receipt function - fetch from API to get accurate data
  const printReceiptFromData = async (receiptData) => {
    try {
      // Fetch sale details from API
      const response = await salesAPI.getById(receiptData.saleId);
      const sale = response.data;

      const printWindow = window.open('', '_blank');

      // Convert logo to base64 if it exists
      let logoBase64 = null;
      if (settings?.store_logo) {
        const logoUrl = `${window.location.origin}${settings.store_logo}`;
        logoBase64 = await imageToBase64(logoUrl);
      }

      const receiptHTML = generateReceiptHTML(sale, logoBase64);

      printWindow.document.write(receiptHTML);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } catch (error) {
      console.error('Print receipt error:', error);
      Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถปริ้นใบเสร็จได้',
        text: 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
        confirmButtonColor: '#0ea5e9'
      });
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'ตะกร้าสินค้าว่างเปล่า',
        text: 'กรุณาเพิ่มสินค้าก่อนทำการชำระเงิน',
        confirmButtonColor: '#0ea5e9'
      });
      return;
    }

    if (paymentMethod === 'cash' && paidAmount < calculateTotal()) {
      const shortage = calculateTotal() - paidAmount;
      Swal.fire({
        icon: 'error',
        title: 'จำนวนเงินที่รับไม่เพียงพอ',
        html: `
          <div class="text-left">
            <p class="mb-2">ยอดที่ต้องชำระ: <strong>฿${calculateTotal().toFixed(2)}</strong></p>
            <p class="mb-2">เงินที่รับมา: <strong>฿${paidAmount.toFixed(2)}</strong></p>
            <p class="text-red-600 font-bold">ขาดอีก: ฿${shortage.toFixed(2)}</p>
          </div>
        `,
        confirmButtonColor: '#0ea5e9'
      });
      return;
    }

    // Send preview to customer display BEFORE confirmation
    const changeAmount = paymentMethod === 'cash' ? calculateChange() : 0;
    socket.emit('payment-preview', {
      total: calculateTotal(),
      paid_amount: paymentMethod === 'cash' ? paidAmount : calculateTotal(),
      change: changeAmount,
      payment_method: paymentMethod
    });

    // Show confirmation dialog before processing payment
    // Wait a bit to prevent Enter key from input field triggering the dialog
    await new Promise(resolve => setTimeout(resolve, 150));

    // Clean up any existing confirm handler before creating new one
    if (confirmKeyHandlerRef.current) {
      document.removeEventListener('keydown', confirmKeyHandlerRef.current, true);
      confirmKeyHandlerRef.current = null;
    }

    let isConfirming = false;

    const confirmResult = await Swal.fire({
      title: 'ยืนยันการชำระเงิน',
      html: `
        <div class="text-left space-y-3">
          <div class="bg-gray-50 p-3 rounded">
            <p class="text-sm text-gray-600">ยอดที่ต้องชำระ</p>
            <p class="text-xl font-bold text-gray-900">฿${calculateTotal().toFixed(2)}</p>
          </div>
          ${paymentMethod === 'cash' ? `
            <div class="bg-blue-50 p-3 rounded">
              <p class="text-sm text-gray-600">รับเงินมา</p>
              <p class="text-xl font-bold text-blue-900">฿${paidAmount.toFixed(2)}</p>
            </div>
            <div class="bg-green-50 p-3 rounded border-2 ${changeAmount > 0 ? 'border-green-500' : 'border-gray-300'}">
              <p class="text-sm text-gray-600">เงินทอน</p>
              <p class="text-3xl font-bold ${changeAmount > 0 ? 'text-green-600' : 'text-gray-500'}">฿${changeAmount.toFixed(2)}</p>
            </div>
          ` : `
            <div class="bg-blue-50 p-3 rounded">
              <p class="text-sm text-gray-600">วิธีชำระ</p>
              <p class="text-lg font-bold text-blue-900">โอนเงิน</p>
            </div>
          `}
          <p class="text-xs text-gray-500 mt-3 text-center">กด Enter เพื่อยืนยัน หรือ ESC เพื่อยกเลิก</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0ea5e9',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'ยืนยัน (Enter)',
      cancelButtonText: 'ยกเลิก (ESC)',
      reverseButtons: true,
      focusConfirm: true,
      allowEnterKey: false,
      allowEscapeKey: false,
      didOpen: () => {
        isDialogOpenRef.current = true;
        confirmKeyHandlerRef.current = (e) => {
          // Only handle if dialog is actually open and visible
          if (!isDialogOpenRef.current || !Swal.isVisible()) {
            return;
          }

          if (isConfirming) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            isConfirming = true;
            Swal.clickConfirm();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            Swal.clickCancel();
          }
        };

        // Add delay before attaching listener
        setTimeout(() => {
          if (confirmKeyHandlerRef.current) {
            document.addEventListener('keydown', confirmKeyHandlerRef.current, true);
          }
        }, 100);
      },
      willClose: () => {
        isDialogOpenRef.current = false;
      },
      didClose: () => {
        if (confirmKeyHandlerRef.current) {
          document.removeEventListener('keydown', confirmKeyHandlerRef.current, true);
          confirmKeyHandlerRef.current = null;
        }
      }
    });

    if (!confirmResult.isConfirmed) {
      // Clear preview from customer display
      socket.emit('clear-preview');
      return;
    }

    setLoading(true);

    try {
      const saleData = {
        customer_name: selectedMember ? selectedMember.full_name : (customerName || 'ลูกค้าทั่วไป'),
        items: cart,
        payment_method: paymentMethod,
        paid_amount: paymentMethod === 'cash' ? paidAmount : calculateTotal(),
        notes: '',
        member_id: selectedMember ? selectedMember.id : null,
        points_used: redeemedPoints,
        points_discount: pointsDiscount,
        cart_discount: cartPromotion ? cartPromotion.discount : 0,
        cart_promotion_id: cartPromotion ? cartPromotion.promotion.id : null,
        manual_discount: calculateManualDiscount(),
        manual_discount_type: manualDiscountType,
      };

      const response = await salesAPI.create(saleData);

      // Notify customer display
      const saleCompleteData = {
        sale_number: response.data.sale_number,
        total: response.data.total_amount,
        paid_amount: paymentMethod === 'cash' ? paidAmount : response.data.total_amount,
        change: response.data.change_amount,
        payment_method: paymentMethod
      };
      console.log('Emitting sale-complete:', saleCompleteData);
      socket.emit('sale-complete', saleCompleteData);

      // Store sale ID for printing
      const receiptData = {
        saleId: response.data.sale_id,
        saleNumber: response.data.sale_number
      };

      // Clean up any existing success handler before creating new one
      if (successKeyHandlerRef.current) {
        document.removeEventListener('keydown', successKeyHandlerRef.current, true);
        successKeyHandlerRef.current = null;
      }

      Swal.fire({
        icon: 'success',
        title: 'ขายสำเร็จ!',
        html: `
          <div class="text-center">
            <p class="text-lg mb-2">เลขที่ใบเสร็จ</p>
            <p class="text-2xl font-bold text-primary-600">${response.data.sale_number}</p>
            ${paymentMethod === 'cash' ? `
              <div class="mt-4 text-left bg-gray-50 p-4 rounded">
                <p>ยอดชำระ: <strong>฿${calculateTotal().toFixed(2)}</strong></p>
                <p>รับเงินมา: <strong>฿${paidAmount.toFixed(2)}</strong></p>
                <p class="text-green-600 font-bold text-xl mt-2">เงินทอน: ฿${response.data.change_amount.toFixed(2)}</p>
              </div>
            ` : ''}
            <p class="text-xs text-gray-500 mt-4">กด Enter เพื่อพิมพ์ใบเสร็จ หรือ ESC เพื่อปิด</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#0ea5e9',
        confirmButtonText: '🖨️ ปริ้นใบเสร็จ (Enter)',
        cancelButtonText: 'ปิด (ESC)',
        reverseButtons: true,
        focusConfirm: true,
        allowEnterKey: false,
        allowEscapeKey: false,
        didOpen: () => {
          isDialogOpenRef.current = true;
          // Add keyboard event listener for this modal
          successKeyHandlerRef.current = (e) => {
            // Only handle if dialog is actually open and visible
            if (!isDialogOpenRef.current || !Swal.isVisible()) {
              return;
            }

            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              Swal.clickConfirm();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              Swal.clickCancel();
            }
          };

          setTimeout(() => {
            if (successKeyHandlerRef.current) {
              document.addEventListener('keydown', successKeyHandlerRef.current, true);
            }
          }, 100);
        },
        willClose: () => {
          isDialogOpenRef.current = false;
        },
        didClose: () => {
          if (successKeyHandlerRef.current) {
            document.removeEventListener('keydown', successKeyHandlerRef.current, true);
            successKeyHandlerRef.current = null;
          }
        }
      }).then((result) => {
        if (result.isConfirmed) {
          // Print receipt
          printReceiptFromData(receiptData);
        }
      });

      // Reset form and clear member info
      setCart([]);
      setCustomerName('');
      setPaidAmount(0);
      setPaymentMethod('cash');
      setManualDiscountType('baht');
      setManualDiscountValue(0);
      setPointsDiscount(0);
      setRedeemedPoints(0);
      setCartPromotion(null);
      setSelectedMember(null);
      setMemberPhone('');

      fetchProducts(); // Refresh product list to update stock

      // Return focus to search input
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 500);
    } catch (error) {
      console.error('Checkout error:', error);
      Swal.fire({
        icon: 'error',
        title: 'ขายไม่สำเร็จ',
        text: error.response?.data?.error || 'ข้อผิดพลาดไม่ทราบสาเหตุ',
        confirmButtonColor: '#0ea5e9'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.barcode?.includes(search)
  );

  return (
    <div className="flex-1 min-h-0 flex flex-row overflow-hidden gap-0">
      {/* Products Section - Left Column */}
      <div className="flex-1 flex flex-col min-h-0 p-4">
        <div className="flex justify-between items-center mb-3 flex-shrink-0">
          <h2 className="text-xl font-bold">หน้าขายสินค้า</h2>
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-green-50 px-3 py-1 rounded-full border border-green-200">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span>พร้อมสแกนบาร์โค้ด</span>
          </div>
        </div>

        <input
          ref={searchInputRef}
          type="text"
          placeholder="ค้นหาสินค้า หรือ สแกนบาร์โค้ด..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full p-3 border rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500 flex-shrink-0"
        />

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid grid-cols-4 gap-3 pb-2">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => {
                  addToCart(product);
                  // Return focus to search input after clicking product
                  setTimeout(() => {
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }, 100);
                }}
                className="bg-white p-3 rounded-lg shadow hover:shadow-lg cursor-pointer transition-shadow"
              >
                <div className="font-semibold text-gray-800 text-sm" style={{
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  minHeight: '2.5rem'
                }}>{product.name}</div>
                <div className="text-xs text-gray-600 mt-1">คงเหลือ: {product.stock_quantity}</div>
                <div className="text-base font-bold text-primary-600 mt-1">
                  ฿{parseFloat(product.selling_price).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cart Items Section - Middle Column */}
      <div className="w-80 bg-white shadow-lg flex flex-col min-h-0 border-l">
        <div className="flex-shrink-0 p-4 border-b">
          <h3 className="text-lg font-bold">รายการสินค้า ({cart.length})</h3>
        </div>

        {/* Cart Items - Scrollable (Max 3 items visible) */}
        <div className="overflow-y-auto p-4" style={{ maxHeight: '270px' }}>
          {cart.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <p>ยังไม่มีสินค้าในตะกร้า</p>
              <p className="text-sm mt-2">เลือกสินค้าหรือสแกนบาร์โค้ด</p>
            </div>
          ) : (
            <div className="space-y-1">
              {cart.map((item) => (
                <div key={item.product_id} className="bg-gray-50 p-2 rounded shadow-sm hover:shadow transition-shadow">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800">{item.product_name}</div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        ฿{item.unit_price.toFixed(2)} × {item.quantity}
                      </div>
                      {item.discount_amount > 0 && (
                        <div className="text-xs text-green-600 mt-0.5 bg-green-50 inline-block px-1.5 py-0.5 rounded">
                          ส่วนลด: -฿{item.discount_amount.toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-2">
                      <div className="font-bold text-sm text-primary-600">
                        ฿{((item.unit_price * item.quantity) - (item.discount_amount || 0)).toFixed(2)}
                      </div>
                      <div className="flex items-center gap-1 mt-1 justify-end">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="bg-red-500 text-white w-6 h-6 rounded hover:bg-red-600 font-bold text-sm"
                        >
                          -
                        </button>
                        <span className="text-xs font-semibold w-5 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="bg-green-500 text-white w-6 h-6 rounded hover:bg-green-600 font-bold text-sm"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Member Section */}
        <div ref={memberSectionRef} className="flex-shrink-0 p-4 border-t">
          <h3 className="text-lg font-bold mb-3">ข้อมูลสมาชิก</h3>

          <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
          <div className="text-sm font-bold text-purple-800 mb-2 flex items-center gap-2">
            <span className="text-lg">👤</span>
            สมาชิก
          </div>
          {!selectedMember ? (
            <div className="space-y-2">
              <input
                type="tel"
                placeholder="เบอร์โทรสมาชิก (แตะเพื่อกรอก)"
                value={memberPhone}
                onClick={() => openKeyboard('phone')}
                onFocus={() => openKeyboard('phone')}
                onChange={(e) => setMemberPhone(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchMember()}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                readOnly
              />
            </div>
          ) : (
            <div className="bg-white p-3 rounded border border-purple-300">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="font-bold text-gray-800">{selectedMember.full_name}</div>
                  <div className="text-sm text-gray-600">{selectedMember.phone}</div>
                </div>
                <button
                  onClick={clearMember}
                  className="text-red-600 hover:text-red-800 text-xl"
                  title="ยกเลิกสมาชิก"
                >
                  ×
                </button>
              </div>
              <div className="pt-2 border-t space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">คะแนนคงเหลือ:</span>
                  <span className="text-lg font-bold text-yellow-600">{selectedMember.points} คะแนน</span>
                </div>
                {cart.length > 0 && calculateTotal() > 0 && (
                  <>
                    <div className="flex justify-between items-center text-green-600">
                      <span className="text-xs">+ ซื้อครั้งนี้ได้:</span>
                      <span className="text-sm font-bold">+{calculateEarnedPoints()} คะแนน</span>
                    </div>
                    {redeemedPoints > 0 && (
                      <div className="flex justify-between items-center text-orange-600">
                        <span className="text-xs">- ใช้คะแนนไป:</span>
                        <span className="text-sm font-bold">-{redeemedPoints} คะแนน</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-1 border-t border-dashed">
                      <span className="text-xs text-gray-700 font-semibold">คะแนนหลังซื้อ:</span>
                      <span className="text-base font-bold text-blue-600">
                        {selectedMember.points + calculateEarnedPoints() - redeemedPoints} คะแนน
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Redeem Points Button */}
              {selectedMember.points >= settings.redemption_points && cart.length > 0 && (
                <button
                  onClick={redeemPoints}
                  className="w-full mt-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded font-semibold flex items-center justify-center gap-2"
                >
                  <span>🎁</span>
                  แลกคะแนน ({settings.redemption_points} คะแนน = {settings.redemption_value} บาท)
                </button>
              )}

              {pointsDiscount > 0 && (
                <div className="mt-2 pt-2 border-t text-sm bg-yellow-50 p-2 rounded">
                  <div className="flex justify-between items-center text-yellow-700">
                    <span>ส่วนลดจากคะแนน:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">-฿{pointsDiscount.toFixed(2)}</span>
                      <button
                        onClick={() => setPointsDiscount(0)}
                        className="text-red-600 hover:text-red-800 text-xs"
                        title="ยกเลิกการแลกคะแนน"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {cart.length > 0 && (
                <div className="mt-2 pt-2 border-t text-sm">
                  <div className="flex justify-between text-green-600">
                    <span>คะแนนที่จะได้รับ:</span>
                    <span className="font-bold">+{calculateEarnedPoints()} คะแนน</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    (ซื้อ {settings.points_earn_baht || 100} บาท = {settings.points_earn_points || 1} คะแนน)
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 w-full">
            <input
              type="text"
              placeholder="ชื่อลูกค้า (ถ้ามี)"
              value={selectedMember ? selectedMember.full_name : customerName}
              onChange={(e) => !selectedMember && setCustomerName(e.target.value)}
              disabled={!!selectedMember}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 text-sm"
            />
          </div>
        </div>
        </div>
      </div>

      {/* Summary + Payment Section - Right Column */}
      <div className="w-96 bg-white shadow-lg flex flex-col min-h-0 border-l overflow-hidden">
        {/* Summary + Payment - Scrollable Section */}
        <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-2 text-sm">
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-bold text-gray-700 mb-2">สรุปยอด</h4>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between font-semibold">
                <span>ยอดรวม:</span>
                <span className="text-gray-800">฿{calculateSubtotal().toFixed(2)}</span>
              </div>

              {/* Discount Breakdown */}
              {calculateDiscount() > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-300">
                  <div className="font-semibold text-gray-700 mb-1">รายละเอียดส่วนลด:</div>

                  {/* Product/Cart Promotion Discounts */}
                  {(cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0) > 0 ||
                    (cartPromotion && cartPromotion.discount > 0)) && (
                    <div className="ml-2 space-y-1">
                      {cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0) > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span className="text-xs">
                            • ส่วนลดสินค้า {selectedMember ? '(สมาชิก)' : '(ลูกค้าทั่วไป)'}:
                          </span>
                          <span>-฿{cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0).toFixed(2)}</span>
                        </div>
                      )}
                      {cartPromotion && cartPromotion.discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span className="text-xs">
                            • ส่วนลดตะกร้า {selectedMember ? '(สมาชิก)' : '(ลูกค้าทั่วไป)'}:
                          </span>
                          <span>-฿{cartPromotion.discount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Points Discount */}
                  {pointsDiscount > 0 && (
                    <div className="ml-2">
                      <div className="flex justify-between text-yellow-700">
                        <span className="text-xs">• ส่วนลดคะแนน (สมาชิก):</span>
                        <span>-฿{pointsDiscount.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Manual Discount */}
                  {calculateManualDiscount() > 0 && (
                    <div className="ml-2">
                      <div className="flex justify-between text-orange-600">
                        <span className="text-xs">
                          • ส่วนลดเพิ่มเติม {selectedMember ? '(สมาชิก)' : '(ลูกค้าทั่วไป)'}:
                        </span>
                        <span>-฿{calculateManualDiscount().toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Total Discount Summary */}
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <div className="bg-green-50 px-3 py-2 rounded-lg">
                      <div className="flex justify-between font-bold text-green-700 mb-2">
                        <span>ส่วนลดรวมทั้งหมด:</span>
                        <span className="text-lg">-฿{calculateDiscount().toFixed(2)}</span>
                      </div>

                      {/* Breakdown by Type */}
                      <div className="space-y-1 text-xs border-t border-green-200 pt-2 mt-1">
                        <div className="flex justify-between items-center">
                          <span className="text-purple-700 font-medium">ส่วนลดสมาชิก:</span>
                          <span className="font-bold text-purple-700">
                            -฿{(
                              (selectedMember ? cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0) : 0) +
                              (selectedMember && cartPromotion ? cartPromotion.discount : 0) +
                              pointsDiscount +
                              (selectedMember ? calculateManualDiscount() : 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">ส่วนลดลูกค้าทั่วไป:</span>
                          <span className="font-bold text-gray-700">
                            -฿{(
                              (!selectedMember ? cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0) : 0) +
                              (!selectedMember && cartPromotion ? cartPromotion.discount : 0) +
                              (!selectedMember ? calculateManualDiscount() : 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Net Total */}
              <div className="flex justify-between text-lg font-bold text-primary-600 mt-2 pt-2 border-t-2 border-gray-400">
                <span>ยอดสุทธิ:</span>
                <span>฿{calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Manual Discount Section */}
          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <div className="text-sm font-semibold text-yellow-800 mb-2">ส่วนลดเพิ่มเติม</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <button
                onClick={() => setManualDiscountType('baht')}
                className={`p-3 rounded-lg font-semibold text-lg transition-all ${
                  manualDiscountType === 'baht'
                    ? 'bg-yellow-500 text-white shadow-lg scale-105'
                    : 'bg-white text-yellow-700 border-2 border-yellow-300 hover:bg-yellow-100'
                }`}
              >
                บาท (฿)
              </button>
              <button
                onClick={() => setManualDiscountType('percent')}
                className={`p-3 rounded-lg font-semibold text-lg transition-all ${
                  manualDiscountType === 'percent'
                    ? 'bg-yellow-500 text-white shadow-lg scale-105'
                    : 'bg-white text-yellow-700 border-2 border-yellow-300 hover:bg-yellow-100'
                }`}
              >
                เปอร์เซ็นต์ (%)
              </button>
            </div>
            <input
              type="number"
              step={manualDiscountType === 'baht' ? '0.01' : '1'}
              min="0"
              max={manualDiscountType === 'percent' ? '100' : undefined}
              value={manualDiscountValue || ''}
              onClick={() => openKeyboard('discount')}
              onFocus={() => openKeyboard('discount')}
              onChange={(e) => setManualDiscountValue(parseFloat(e.target.value) || 0)}
              placeholder="แตะเพื่อกรอกจำนวน"
              className="w-full p-3 border-2 rounded-lg text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-500 cursor-pointer text-center"
              readOnly
            />
          </div>

          {/* Payment Method */}
          <div className="bg-white p-3 rounded-lg border">
            <label className="block text-sm font-semibold mb-2">วิธีชำระเงิน</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPaymentMethod('cash')}
                className={`p-4 rounded-lg font-semibold text-lg transition-all ${
                  paymentMethod === 'cash'
                    ? 'bg-green-500 text-white shadow-lg scale-105'
                    : 'bg-white text-green-700 border-2 border-green-300 hover:bg-green-100'
                }`}
              >
                <div className="text-2xl mb-1">💵</div>
                <div>เงินสด</div>
              </button>
              <button
                onClick={() => setPaymentMethod('transfer')}
                className={`p-4 rounded-lg font-semibold text-lg transition-all ${
                  paymentMethod === 'transfer'
                    ? 'bg-blue-500 text-white shadow-lg scale-105'
                    : 'bg-white text-blue-700 border-2 border-blue-300 hover:bg-blue-100'
                }`}
              >
                <div className="text-2xl mb-1">🏦</div>
                <div>โอนเงิน</div>
              </button>
            </div>
          </div>

          {paymentMethod === 'cash' && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-3">
              <label className="block text-sm font-semibold mb-2 text-blue-900">รับเงินมา</label>

              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <button
                  onClick={() => setPaidAmount(20)}
                  className="p-4 bg-white hover:bg-blue-100 active:bg-blue-200 border-2 border-blue-300 rounded-lg font-bold text-lg transition-all transform active:scale-95 shadow-sm"
                >
                  ฿20
                </button>
                <button
                  onClick={() => setPaidAmount(50)}
                  className="p-4 bg-white hover:bg-blue-100 active:bg-blue-200 border-2 border-blue-300 rounded-lg font-bold text-lg transition-all transform active:scale-95 shadow-sm"
                >
                  ฿50
                </button>
                <button
                  onClick={() => setPaidAmount(100)}
                  className="p-4 bg-white hover:bg-blue-100 active:bg-blue-200 border-2 border-blue-300 rounded-lg font-bold text-lg transition-all transform active:scale-95 shadow-sm"
                >
                  ฿100
                </button>
                <button
                  onClick={() => setPaidAmount(500)}
                  className="p-4 bg-white hover:bg-blue-100 active:bg-blue-200 border-2 border-blue-300 rounded-lg font-bold text-lg transition-all transform active:scale-95 shadow-sm"
                >
                  ฿500
                </button>
                <button
                  onClick={() => setPaidAmount(1000)}
                  className="p-4 bg-white hover:bg-blue-100 active:bg-blue-200 border-2 border-blue-300 rounded-lg font-bold text-lg transition-all transform active:scale-95 shadow-sm"
                >
                  ฿1,000
                </button>
                <button
                  onClick={() => setPaidAmount(Math.ceil(calculateTotal()))}
                  className="p-4 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white border-2 border-green-600 rounded-lg font-bold text-sm transition-all transform active:scale-95 shadow-sm"
                >
                  พอดี
                </button>
              </div>

              {/* Custom Amount Input */}
              <div>
                <label className="block text-xs font-semibold mb-1 text-blue-800">กรอกเองหรือแก้ไข</label>
                <input
                  type="number"
                  step="0.01"
                  value={paidAmount || ''}
                  onClick={() => openKeyboard('paid')}
                  onFocus={() => openKeyboard('paid')}
                  onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCheckout();
                    }
                  }}
                  placeholder="แตะเพื่อกรอก"
                  className="w-full p-4 border-2 border-blue-300 rounded-lg text-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer text-center bg-white"
                  readOnly
                />
              </div>

              {/* Change Display */}
              <div className={`p-4 rounded-lg border-2 ${
                calculateChange() > 0
                  ? 'bg-green-50 border-green-500'
                  : 'bg-gray-50 border-gray-300'
              }`}>
                <p className="text-sm text-gray-600 mb-1">เงินทอน</p>
                <p className={`text-3xl font-bold ${
                  calculateChange() > 0
                    ? 'text-green-600'
                    : 'text-gray-500'
                }`}>
                  ฿{calculateChange().toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {paymentMethod === 'transfer' && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-semibold text-center">
                📱 แสดง QR Code ที่หน้าจอลูกค้า
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons - Fixed at Bottom */}
        <div className="flex-shrink-0 p-4 border-t bg-gray-50 space-y-2">
          <button
            onClick={handleCheckout}
            disabled={loading || cart.length === 0}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-4 rounded-lg disabled:opacity-50 transition-colors text-base shadow-lg hover:shadow-xl"
          >
            {loading ? 'กำลังดำเนินการ...' : paymentMethod === 'cash' ? '💰 ชำระเงิน (Enter)' : '💳 ชำระเงิน'}
          </button>

          <button
            onClick={() => {
              setCart([]);
              setManualDiscountValue(0);
              setPointsDiscount(0);
              setRedeemedPoints(0);
              setCartPromotion(null);
              // Keep member logged in
            }}
            disabled={cart.length === 0}
            className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg transition-colors text-sm disabled:opacity-50"
          >
            🗑️ ล้างตะกร้า
          </button>
        </div>
      </div>

      {/* Numeric Keyboard Modal */}
      {showKeyboard && (
        <NumericKeyboard
          value={keyboardValue}
          onChange={handleKeyboardChange}
          onClose={closeKeyboard}
          allowDecimal={keyboardType === 'paid' || keyboardType === 'discount'}
          maxLength={keyboardType === 'phone' ? 10 : null}
          isPhoneNumber={keyboardType === 'phone'}
          quickAmounts={
            keyboardType === 'paid' ? [
              { label: '฿20', value: 20 },
              { label: '฿50', value: 50 },
              { label: '฿100', value: 100 },
              { label: '฿500', value: 500 },
              { label: '฿1000', value: 1000 }
            ] : keyboardType === 'discount' ? (
              manualDiscountType === 'baht' ? [
                { label: '฿5', value: 5 },
                { label: '฿10', value: 10 },
                { label: '฿20', value: 20 },
                { label: '฿50', value: 50 },
                { label: '฿100', value: 100 }
              ] : [
                { label: '2%', value: 2 },
                { label: '5%', value: 5 },
                { label: '10%', value: 10 },
                { label: '15%', value: 15 },
                { label: '20%', value: 20 }
              ]
            ) : null
          }
          label={keyboardType === 'phone' ? 'กรุณากรอกเบอร์โทรของสมาชิก' : 'จำนวน'}
        />
      )}
    </div>
  );
}

export default POS;
